import { addItemPage as addDynamicItemPage } from '../add-dynamic-item-page/addDynamicItemPage'
import { addPageAndReplaceCollection as addPageWithRepeater } from '../add-page-with-repeater/addPageWithRepeater'
import { getProgressBar } from '../../progress-bar/executeSteps'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import { NotificationType } from '@wix/editor-platform-sdk-types'
import { truncateCollectionName } from '../truncateCollectionName'

import {
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
} from '@wix/wix-data-client-common'
import type {
  Editor3DynamicPagesBiParams,
  AppApi,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { PresetDefinition } from '../../presets/types'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import { addMainSlugFieldIfNotExists } from '../../slug/addSlugField'
import { generateUniquePageLinkId } from '../../slug/generatePageLinkId'
import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'
import type { Schema } from '@wix/wix-data-schema-types'

type Params = {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  preset: PresetDefinition
  collectionsApi: CollectionsApi
  existingCollectionId: string
  i18n: I18n
  fedopsLogger: FedopsLogger
  logger: Logger
  biParams: Editor3DynamicPagesBiParams
}

export type PageAddResult = {
  pageRef: PageRef
  schema: Schema
  newRouterPrefix?: string
  pattern?: string
}

const interactionName = 'addCmsPages'

export const addCmsPages = async ({
  editorSdkProxy,
  appApi,
  preset,
  collectionsApi,
  existingCollectionId,
  i18n,
  fedopsLogger,
  logger,
  biParams,
}: Params) => {
  fedopsLogger.interactionStarted(interactionName)

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 2,
    title: i18n.t('Editor3_Add_Pages_Progress_Bar_Title_Creating_Pages'),
  })
  await progressBar.open()

  const addPageResults: PageAddResult[] = []

  try {
    const newCollectionSchema = await collectionsApi.get(existingCollectionId)

    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Adding_List_Page',
        {
          collection_name: truncateCollectionName(
            newCollectionSchema.displayName,
          ),
        },
      ),
    })

    await addMainSlugFieldIfNotExists({
      collectionsApi,
      collectionId: newCollectionSchema.id,
    })

    const pageLinkId = await generateUniquePageLinkId({ editorSdkProxy })

    await runTransactionWithRetry(editorSdkProxy, async () => {
      const listPageResult = await addPageWithRepeater({
        editorSdkProxy,
        appApi,
        preset,
        collectionsApi,
        existingCollectionId,
        pageLinkFieldKey: pageLinkId,
      })

      addPageResults.push(listPageResult)

      await progressBar.update({
        stepTitle: i18n.t(
          'Editor3_Add_Pages_Bundle_Progress_Bar_Step_Title_Adding_Items_Page',
          {
            collection_name: truncateCollectionName(
              newCollectionSchema.displayName,
            ),
          },
        ),
      })

      const itemPageResult = await addDynamicItemPage({
        editorSdkProxy,
        appApi,
        preset,
        collectionsApi,
        existingCollectionId,
        pageLinkId,
      })

      addPageResults.push(itemPageResult)
    })

    await progressBar.update()
  } finally {
    await progressBar.close()
  }

  const [listPageResult, itemPageResult] = addPageResults
  const { pageRef: listPageRef, schema: listPageSchema } = listPageResult
  const {
    pageRef: itemPageRef,
    schema: itemPageSchema,
    newRouterPrefix,
    pattern,
  } = itemPageResult

  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      page_type: 'page_with_repeater',
      collection_name: listPageSchema.displayName,
      collection_id: existingCollectionId,
      is_auto_bound: true,
      new_collection: false,
      pageId: listPageRef.id,
      action_type: 'add_dynamic_page',
      origin: biParams?.origin,
      template_id: preset.id,
    }),
  )

  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      page_type: 'item',
      collection_name: itemPageSchema.displayName,
      collection_id: existingCollectionId,
      new_url: `${newRouterPrefix}${pattern}`,
      is_auto_bound: true,
      new_collection: false,
      pageId: itemPageRef.id,
      action_type: 'add_dynamic_page',
      origin: biParams?.origin,
      template_id: preset.id,
    }),
  )

  await appApi.handleChangedRouters({ pageRefs: [itemPageRef] })

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.UPDATE_ROUTERS,
    params: { pageRefs: [itemPageRef] },
  })

  fedopsLogger.interactionEnded(interactionName)

  await editorSdkProxy.pages.navigateTo({ pageRef: listPageRef })

  appApi.showToastNotification(
    {
      message: i18n.t('Editor3_Add_Pages_Bundle_Toast_Success_Message'),
      type: NotificationType.Success,
      linkText: i18n.t('Editor3_Add_Pages_Bundle_Toast_Success_Link_Text'),
    },
    () => {
      editorSdkProxy.pages.navigateTo({ pageRef: itemPageRef })
    },
  )

  return { listPageRef, itemPageRef }
}
